declare global {
  interface Window {
    fbq?: (
      action: "track" | "trackCustom" | "init",
      eventOrPixelId: string,
      params?: Record<string, unknown>,
      options?: { eventID?: string },
    ) => void;
  }
}

import { getOrCreateExternalId, readStoredFbc } from "@/lib/attribution";
import { mirrorToAxon, setAxonUserData } from "@/lib/axon";

const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID || "1907867539888146";

type AdvancedMatchingData = {
  em?: string;
  fn?: string;
  ln?: string;
  ph?: string;
  ct?: string;
  st?: string;
  zp?: string;
  country?: string;
  external_id?: string;
};

function readCookie(name: string) {
  if (typeof document === "undefined") {
    return undefined;
  }

  const value = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");

  return value ? decodeURIComponent(value) : undefined;
}

export function getMarketingCookies() {
  return {
    fbc: readCookie("_fbc") ?? readStoredFbc(),
    fbp: readCookie("_fbp"),
    externalId: getOrCreateExternalId(),
  };
}

function createEventId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `vellum-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

type FbqArgs = [
  action: "track" | "trackCustom" | "init",
  eventOrPixelId: string,
  params?: Record<string, unknown>,
  options?: { eventID?: string },
];

// The Meta pixel stub is injected by <Script strategy="afterInteractive"> in the root layout, so
// window.fbq does NOT exist yet while a mount effect runs. Calls made in that window used to be
// dropped on the floor, and that — not the old ?new=1 gate — is why Purchase never reached Meta:
// the order-status page fires it synchronously on mount, whereas ViewContent/AddToCart/
// InitiateCheckout all happen after a fetch or a user click and so always found fbq ready.
// Buffer anything that arrives early and replay it in order once the stub appears; fbq's own
// queue then holds it until DeferredThirdParty loads fbevents.js at interaction/idle.
const pendingFbqCalls: FbqArgs[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushAttempts = 0;
const MAX_FLUSH_ATTEMPTS = 40; // ~10s at 250ms; the stub normally lands within a tick of hydration

function drainFbqQueue() {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return false;
  while (pendingFbqCalls.length > 0) {
    const args = pendingFbqCalls.shift() as FbqArgs;
    window.fbq(...args);
  }
  return true;
}

function scheduleDrain() {
  if (flushTimer !== null || pendingFbqCalls.length === 0) return;
  if (flushAttempts >= MAX_FLUSH_ATTEMPTS) return;
  flushAttempts += 1;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    if (!drainFbqQueue()) scheduleDrain();
  }, 250);
}

function callFbq(...args: FbqArgs) {
  if (typeof window === "undefined") return;
  // Once anything is queued everything queues, so an "init" advanced-matching call can never be
  // reordered after the "track" it was meant to decorate.
  if (pendingFbqCalls.length === 0 && typeof window.fbq === "function") {
    window.fbq(...args);
    return;
  }
  pendingFbqCalls.push(args);
  if (!drainFbqQueue()) scheduleDrain();
}

export function initAdvancedMatching(data: AdvancedMatchingData) {
  if (typeof window === "undefined") return;

  // AppLovin takes the same identity, on the event itself rather than on an init call.
  setAxonUserData(data.em);

  // Always carry the stable external_id so every event matches on it, even when
  // no PII is available for this particular call.
  const withExternalId: AdvancedMatchingData = {
    external_id: getOrCreateExternalId(),
    ...data,
  };

  const cleanData = Object.fromEntries(
    Object.entries(withExternalId).filter(([, value]) => value !== undefined && value !== ""),
  );

  if (Object.keys(cleanData).length > 0) {
    callFbq("init", META_PIXEL_ID, cleanData);
  }
}

/**
 * Re-initialise the Pixel with the stable external_id as advanced-matching data
 * so it attaches to every subsequent client event (PageView, ViewContent, …).
 * Safe to call on every page load — fbq merges advanced-matching data on init.
 */
export function initPixelIdentity() {
  initAdvancedMatching({});
}

function track(eventName: string, payload: Record<string, unknown> = {}, eventId = createEventId()) {
  if (typeof window === "undefined") return eventId;

  const prefix = window.location.pathname.match(/^\/(nl|de|en|uk)\b/)?.[1] ?? "nl";
  const locale = prefix === "uk" ? "en-GB" : prefix;
  const { fbc, fbp } = getMarketingCookies();

  callFbq(
    "track",
    eventName,
    {
      ...payload,
      locale,
      ...(fbc ? { fbc } : {}),
      ...(fbp ? { fbp } : {}),
    },
    { eventID: eventId },
  );

  // AppLovin optimises on the same five funnel events, so every Meta event is mirrored to the Axon
  // pixel here with the shared event id — one call site, one payload, two networks.
  mirrorToAxon(eventName, payload, eventId);

  return eventId;
}

export function trackPageView(path?: string) {
  return track("PageView", path ? { page_path: path } : {});
}

export function trackViewContent(payload: Record<string, unknown> = {}, eventId?: string) {
  return track("ViewContent", payload, eventId);
}

export function trackAddToCart(payload: Record<string, unknown> = {}, eventId?: string) {
  return track("AddToCart", payload, eventId);
}

export function trackInitiateCheckout(payload: Record<string, unknown> = {}, eventId?: string) {
  return track("InitiateCheckout", payload, eventId);
}

export function trackPurchase(payload: Record<string, unknown> = {}, eventId?: string) {
  return track("Purchase", payload, eventId);
}
